import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { authAPI } from '../services/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is logged in on app start
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const userId = localStorage.getItem('user_id');
        
        if (token && userId) {
          // Validate token and get user info with access token
          const userData = await authAPI.getCurrentUser(userId, token);
          if (userData && (userData.role === 'admin' || userData.userType === 'admin')) {
            setUser(userData);
            setIsAuthenticated(true);
          } else {
            // Not an admin user, clear token
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_id');
            toast.error('Access denied. Admin privileges required.');
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_id');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      setIsLoading(true);
      
      // Step 1: Login and get token
      const loginResponse = await authAPI.login(email, password, rememberMe);
      
      // The response contains: { id: "token", userId: "userId", ttl: 1209600, created: "date" }
      const { id: token, userId } = loginResponse;
      
      if (!token || !userId) {
        throw new Error('Invalid login response');
      }
      
      // Step 2: Get user details using the userId and access token
      const userResponse = await authAPI.getCurrentUser(userId, token);
      const userData = userResponse;
      
      // Check if user is admin (adjust this based on your user model)
      if (userData.role !== 'admin' && userData.userType !== 'admin') {
        throw new Error('Access denied. Admin privileges required.');
      }

      // Store token and userId
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user_id', userId);
      
      // Set user data
      setUser(userData);
      setIsAuthenticated(true);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.error?.message || error.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      const response = await authAPI.register(userData);
      
      toast.success('Registration successful! Please login.');
      return { success: true, data: response };
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.error?.message || error.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await authAPI.logout(token);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_id');
      setUser(null);
      setIsAuthenticated(false);
      toast.success('Logged out successfully');
    }
  };

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const userId = localStorage.getItem('user_id');
      
      if (!token || !userId) return false;

      // Get user data using the stored userId and access token
      const userData = await authAPI.getCurrentUser(userId, token);
      
      if (userData && (userData.role === 'admin' || userData.userType === 'admin')) {
        setUser(userData);
        setIsAuthenticated(true);
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
      return false;
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};